import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { dynamicFormFields } from "./form-conf";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";

const filterN1PlansSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  product_equal: z.string().optional(),
  asset_class_1_equal: z.string().optional(),
  asset_class_2_equal: z.string().optional(),
  sector_equal: z.string().optional(),
  region_equal: z.string().optional(),
  lpt_fund_vintage_start_date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  lpt_fund_vintage_start_date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  lpt_investment_period_end_date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  lpt_investment_period_end_date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

const filterN1PlansFields = filterN1PlansSchema.keyof().enum;

export type FilterN1PlansSchema = z.infer<typeof filterN1PlansSchema>;

export function FilterN1Plans() {
  const { t, i18n } = useTranslation("tables");

  const n1PlanDepFields: readonly DepField<FilterN1PlansSchema>[] = [
    {
      name: filterN1PlansFields.name_contains,
      key: dynamicFormFields.name,
      extract: "text",
      bucket: "equals",
    },
  ];

  const fields: Field[] = [
    {
      name: filterN1PlansFields.name_contains,
      label: t(`${Tables.N1_PLANS}.columns.name`),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_PLANS}`,
      column_name: dynamicFormFields.name,
      filters: makeFiltersBuilder<FilterN1PlansSchema>(
        n1PlanDepFields,
        "name_contains",
      ),
    },
    {
      name: filterN1PlansFields.product_equal,
      label: t(`${Tables.N1_PLANS}.columns.${dynamicFormFields.product}`),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: Tables.N1_PLANS,
      language: "fr",
      translate_key: dynamicFormFields.product,
    },
    {
      name: filterN1PlansFields.asset_class_1_equal,
      label: t(`${Tables.N1_PLANS}.columns.${dynamicFormFields.asset_class_1}`),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: Tables.N1_PLANS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: dynamicFormFields.asset_class_1,
    },
    {
      name: filterN1PlansFields.asset_class_2_equal,
      label: t(`${Tables.N1_PLANS}.columns.${dynamicFormFields.asset_class_2}`),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: Tables.N1_PLANS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: dynamicFormFields.asset_class_2,
    },
    {
      name: filterN1PlansFields.sector_equal,
      label: t(`${Tables.N1_PLANS}.columns.${dynamicFormFields.sector}`),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: Tables.N1_PLANS,
      language: "fr",
      translate_key: dynamicFormFields.sector,
    },
    {
      name: filterN1PlansFields.region_equal,
      label: t(`${Tables.N1_PLANS}.columns.${dynamicFormFields.region}`),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: Tables.N1_PLANS,
      language: "fr",
      translate_key: dynamicFormFields.region,
    },
    {
      name: "date",
      type: "dateRange",
      label: t(
        `${Tables.N1_PLANS}.columns.${dynamicFormFields.lpt_fund_vintage_start_date}`,
      ),
      from: { name: filterN1PlansFields.lpt_fund_vintage_start_date_from },
      to: { name: filterN1PlansFields.lpt_fund_vintage_start_date_to },
      copy_from_to_when_to_empty: true,
      col_span: 2,
    },
    {
      name: "date",
      type: "dateRange",
      label: t(
        `${Tables.N1_PLANS}.columns.${dynamicFormFields.lpt_investment_period_end_date}`,
      ),
      from: { name: filterN1PlansFields.lpt_investment_period_end_date_from },
      to: { name: filterN1PlansFields.lpt_investment_period_end_date_to },
      copy_from_to_when_to_empty: true,
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.N1}
      schema={filterN1PlansSchema}
      tableName={`${Tables.N1_PLANS}`}
      fields={fields}
    />
  );
}
